import React, { useRef } from "react";
import "./markdownEditor.scss";

const MarkdownEditor = ({ value = "", onChange, name, placeholder = "" }) => {
  const textareaRef = useRef(null);

  const insertMarkdown = (before, after = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newValue =
      value.substring(0, start) +
      before +
      selectedText +
      after +
      value.substring(end);

    onChange(newValue);

    // Focus back on textarea and position cursor
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + before.length + selectedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const insertHeading = (level) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const lineStart =
      value.lastIndexOf("\n", start - 1) === -1
        ? 0
        : value.lastIndexOf("\n", start - 1) + 1;

    const heading = "#".repeat(level) + " ";
    const beforeCursor = value.substring(0, lineStart);
    const afterCursor = value.substring(lineStart);

    const newValue = beforeCursor + heading + afterCursor;
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        lineStart + heading.length,
        lineStart + heading.length
      );
    }, 0);
  };

  const insertLine = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const lineStart =
      value.lastIndexOf("\n", start - 1) === -1
        ? 0
        : value.lastIndexOf("\n", start - 1) + 1;

    const beforeCursor = value.substring(0, lineStart);
    const afterCursor = value.substring(lineStart);

    const newValue = beforeCursor + "---\n" + afterCursor;
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(lineStart + 4, lineStart + 4);
    }, 0);
  };

  const insertNewLine = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const newValue = value.substring(0, start) + "\n" + value.substring(start);
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 1, start + 1);
    }, 0);
  };

  const ToolbarButton = ({ onClick, title, children }) => (
    <button
      type="button"
      className="markdown-toolbar__button"
      onClick={onClick}
      title={title}
      aria-label={title}
    >
      {children}
    </button>
  );

  return (
    <div className="markdown-editor">
      <div className="markdown-toolbar">
        <div className="markdown-toolbar__group">
          <ToolbarButton
            onClick={() => insertMarkdown("**", "**")}
            title="Bold"
          >
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => insertMarkdown("*", "*")}
            title="Italic"
          >
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => insertMarkdown("__", "__")}
            title="Underline"
          >
            <u>U</u>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => insertMarkdown("~~", "~~")}
            title="Strikethrough"
          >
            <s>S</s>
          </ToolbarButton>
        </div>

        <div className="markdown-toolbar__group">
          <ToolbarButton onClick={() => insertHeading(1)} title="Heading 1">
            H1
          </ToolbarButton>
          <ToolbarButton onClick={() => insertHeading(2)} title="Heading 2">
            H2
          </ToolbarButton>
          <ToolbarButton onClick={() => insertHeading(3)} title="Heading 3">
            H3
          </ToolbarButton>
        </div>

        <div className="markdown-toolbar__group">
          <ToolbarButton onClick={insertNewLine} title="New Line">
            ↵
          </ToolbarButton>
          <ToolbarButton onClick={insertLine} title="Horizontal Rule">
            ―
          </ToolbarButton>
        </div>
      </div>

      <textarea
        ref={textareaRef}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="markdown-editor__textarea"
      />

      <div className="markdown-editor__preview-hint">
        <small>Markdown syntax supported: **bold**, *italic*, __underline__, ~~strikethrough~~, # Heading 1-3, ---, and line breaks</small>
      </div>
    </div>
  );
};

export default MarkdownEditor;
